import { useEffect } from 'react';

// Locks the host page's scroll while the chat covers the viewport on mobile.
// `overflow: hidden` on body does not stop iOS touch scrolling; fixing the
// body at the current offset does, and the offset is restored on unlock.
// Reference-counted: in floating (lib) mode the launcher and the conversation
// are separate React roots and must not unlock each other.

type SavedStyles = {
    position: string;
    top: string;
    left: string;
    width: string;
    overflow: string;
    scrollY: number;
};

let lockCount = 0;
let saved: SavedStyles | null = null;

export function lockBodyScroll(): void {
    lockCount += 1;
    if (lockCount > 1 || typeof document === 'undefined') return;

    const { style } = document.body;
    saved = {
        position: style.position,
        top: style.top,
        left: style.left,
        width: style.width,
        overflow: style.overflow,
        scrollY: window.scrollY || 0,
    };
    style.position = 'fixed';
    style.top = `-${saved.scrollY}px`;
    style.left = '0';
    style.width = '100%';
    style.overflow = 'hidden';
}

export function unlockBodyScroll(): void {
    if (lockCount === 0) return;
    lockCount -= 1;
    if (lockCount > 0 || !saved) return;

    const { style } = document.body;
    style.position = saved.position;
    style.top = saved.top;
    style.left = saved.left;
    style.width = saved.width;
    style.overflow = saved.overflow;
    // 'instant' bypasses a host's `scroll-behavior: smooth`, which would animate
    // the restore from the top.
    window.scrollTo({ top: saved.scrollY, left: 0, behavior: 'instant' } as ScrollToOptions);
    saved = null;
}

export function useBodyScrollLock(active: boolean): void {
    useEffect(() => {
        if (!active) return undefined;
        lockBodyScroll();
        return () => unlockBodyScroll();
    }, [active]);
}

export function __resetScrollLockForTests(): void {
    lockCount = 0;
    saved = null;
}
